import { v4 as uuidv4 } from 'uuid';
import api from '../../../app/api/axios-api';
import {
  collectAnalyzedUploadState,
  filterPendingUploads,
  isLlmUploadGroupAlreadyAnalyzed,
  mergeProjectResultsIntoProjectData,
  normalizeUploadBaseTitle
} from './analysisUploadFilter';

/** ML/LLM analysis endpoints often exceed the default 30s axios timeout. */
const PROJECT_ANALYSIS_REQUEST_CONFIG = { timeout: 300000 };

/**
 * Policy Violation can exceed 5m when Layer 3 + FAISS evidence run for many
 * flagged pairs. Backend (Cloud Run) allows up to ~1h; keep client under that.
 */
const POLICY_VIOLATION_REQUEST_CONFIG = { timeout: 600_000 };

interface ProjectData {
  projectId?: string;
  userId?: string;
  domain?: string;
  title?: string;
  uploads?: {
    dataset?: any[];
    ml_model?: any[];
    llm_models?: any[];
    policy_document?: any[];
  };
}

interface BuildMlBiasPayloadResult {
  title: string;
  data: any[];
}

const buildMlBiasPayload = (
  mlModels: any[],
  datasets: any[],
  projectId: string,
  userId: string,
  domain: string,
  fallbackTitle?: string | null
): BuildMlBiasPayloadResult => {
  const normalizedMlModels = mlModels.map((model: any, idx: number) => {
    const title = model?.title || model?.tag || model?.file_name || `ML Model ${idx + 1}`;
    return {
      ...model,
      projectId: model?.projectId && model.projectId !== '' ? model.projectId : projectId,
      userId: model?.userId && model.userId !== '' ? model.userId : userId,
      domain: model?.domain && model.domain !== '' ? model.domain : domain,
      title
    };
  });

  const normalizedDatasets = datasets.map((item: any, idx: number) => {
    const title = item?.title || item?.file_name || `Dataset ${idx + 1}`;
    return {
      ...item,
      projectId: item?.projectId && item.projectId !== '' ? item.projectId : projectId,
      userId: item?.userId && item.userId !== '' ? item.userId : userId,
      domain: item?.domain && item.domain !== '' ? item.domain : domain,
      title
    };
  });

  const data = [...normalizedMlModels, ...normalizedDatasets];

  const title = normalizedMlModels[0]?.title || normalizedDatasets[0]?.title || fallbackTitle || 'ML Model';

  return { title, data };
};

const normalizeTimestamp = (value: any) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && '$date' in value) {
    return value.$date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/**
 * Normalize prompt_outputs so the backend never receives a dict (avoids Python "'dict' object has no attribute 'lower'").
 * In Mongo, prompt_outputs can be an object like { study_context: [...], baseline_summary: [...] }.
 * Flatten to a single array of strings for API payloads.
 */
const normalizePromptOutputs = (value: any): string[] => {
  if (value == null) return [];
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  if (typeof value === 'object') {
    const out: string[] = [];
    for (const v of Object.values(value)) {
      if (typeof v === 'string') out.push(v);
      else if (Array.isArray(v)) out.push(...v.filter((x): x is string => typeof x === 'string'));
    }
    return out;
  }
  return [];
};

/** Coerce upload lists that may be a single object instead of an array. */
const asUploadArray = (value: unknown): any[] => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value];
  return [];
};

/** Unwrap nested Mongo / api_direct_submission metadata (prompt often on inner `model_metadata`). */
const unwrapLlmModelMetadata = (entry: Record<string, unknown>): Record<string, unknown> | undefined => {
  const meta = entry.model_metadata as Record<string, unknown> | undefined;
  if (!meta) return undefined;

  // Life Sciences uploads: { model_metadata: { ..., model_metadata: { prompt, ... } } }
  const inner = meta.model_metadata;
  if (inner && typeof inner === 'object') {
    const innerRow = inner as Record<string, unknown>;
    if (innerRow.prompt || innerRow.prompt_outputs || innerRow.examples) {
      return innerRow;
    }
  }
  return meta;
};

/** Read prompt text from common upload / API shapes (including prompt-only `data: [{ prompt }]`). */
const resolveEntryPrompt = (entry: unknown): string | undefined => {
  if (!entry) return undefined;
  if (typeof entry === 'string' && entry.trim()) return entry;
  if (typeof entry !== 'object') return undefined;

  const row = entry as Record<string, unknown>;
  const innerMeta = unwrapLlmModelMetadata(row);
  const outerMeta = row.model_metadata as Record<string, unknown> | undefined;
  const candidates = [
    row.prompt,
    row.Prompt,
    row.user_prompt,
    row.input,
    row.text,
    row.content,
    outerMeta?.prompt,
    outerMeta?.Prompt,
    innerMeta?.prompt,
    innerMeta?.Prompt
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
  }
  return undefined;
};

/** Prefer a real storage URL over the api_direct_submission placeholder. */
const resolveEntryFileUrl = (entry: Record<string, unknown>): string | undefined => {
  const outerMeta = entry.model_metadata as Record<string, unknown> | undefined;
  const innerMeta = unwrapLlmModelMetadata(entry);
  const candidates = [entry.file_url, outerMeta?.file_url, innerMeta?.file_url];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() && candidate !== 'api_direct_submission') {
      return candidate;
    }
  }
  return undefined;
};

/** Flatten nested `data` / array wrappers and string rows into prompt entry objects. */
const flattenPromptEntryList = (items: unknown[]): any[] =>
  items.flatMap(item => {
    if (typeof item === 'string' && item.trim()) return [{ prompt: item }];
    if (Array.isArray(item)) return flattenPromptEntryList(item);
    if (item && typeof item === 'object') {
      const row = item as Record<string, unknown>;
      if (Array.isArray(row.data)) return flattenPromptEntryList(row.data);
      if (Array.isArray(row.entries)) return flattenPromptEntryList(row.entries);
      if (Array.isArray(row.prompts)) return flattenPromptEntryList(row.prompts);
      return [item];
    }
    return [];
  });

/** Parse list fields that may be arrays, JSON strings, or nested objects. */
const parseLlmEntryList = (value: unknown): any[] => {
  if (value == null) return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return flattenPromptEntryList(parsed);
      if (parsed && typeof parsed === 'object') return flattenPromptEntryList([parsed]);
    } catch {
      return [{ prompt: trimmed }];
    }
    return [];
  }
  if (Array.isArray(value)) return flattenPromptEntryList(value);
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj.data)) return flattenPromptEntryList(obj.data);
    if (Array.isArray(obj.prompts)) return flattenPromptEntryList(obj.prompts);
    if (Array.isArray(obj.entries)) return flattenPromptEntryList(obj.entries);
    if (resolveEntryPrompt(obj) || obj.prompt_outputs || obj.file_url) return [value];
  }
  return [];
};

/**
 * TSC/MTC uploads may store LLM rows grouped as `{ data: [...] }` or as flat file records
 * on `uploads.llm_model` without a nested `data` array.
 */
const isPromptRow = (value: unknown): boolean => Boolean(resolveEntryPrompt(value));

/** Unwrap nested prompt arrays (e.g. parsed LS.Prompt(80).json stored on `content`). */
const expandLlmUploadRecords = (llmModels: any[]): any[] =>
  llmModels.flatMap(model => {
    if (Array.isArray(model) && model.length > 0 && model.every(isPromptRow)) {
      return model;
    }
    return [model];
  });

const llmUploadDedupeKey = (model: any): string | null => {
  if (!model || typeof model !== 'object') return null;
  if (model.upload_id) return `id:${String(model.upload_id)}`;
  const fileUrl = resolveEntryFileUrl(model as Record<string, unknown>);
  if (fileUrl) return `url:${fileUrl}`;
  if (model.file_name) return `file:${String(model.file_name)}`;
  if (model.file_url && model.file_url !== 'api_direct_submission') return `url:${String(model.file_url)}`;
  const titleKey = normalizeUploadBaseTitle(model.title || model.tag);
  if (titleKey) return `title:${titleKey}`;
  return null;
};

/** Same LLM file may appear under llm_models, llm_model, and LLM_MODEL after Copilot uploads. */
const dedupeLlmUploadRecords = (llmModels: any[]): any[] => {
  const seen = new Set<string>();
  const result: any[] = [];

  for (const model of llmModels) {
    const key = llmUploadDedupeKey(model);
    if (!key) {
      result.push(model);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(model);
  }

  return result;
};

const collectLlmModelEntries = (llmModel: any): any[] => {
  if (!llmModel) return [];
  if (typeof llmModel === 'string') return parseLlmEntryList(llmModel);
  if (typeof llmModel !== 'object') return [];

  const fromData = parseLlmEntryList(llmModel.data);
  if (fromData.length > 0) return fromData;
  const fromEntries = parseLlmEntryList(llmModel.entries);
  if (fromEntries.length > 0) return fromEntries;
  // Parsed Life Sciences / TSC prompt files (e.g. LS.Prompt(80).json) may land on `prompts`
  const fromPrompts = parseLlmEntryList(llmModel.prompts);
  if (fromPrompts.length > 0) return fromPrompts;
  const fromContent = parseLlmEntryList(llmModel.content);
  if (fromContent.length > 0) return fromContent;
  const fromParsedData = parseLlmEntryList(llmModel.parsed_data);
  if (fromParsedData.length > 0) return fromParsedData;
  const fromItems = parseLlmEntryList(llmModel.items);
  if (fromItems.length > 0) return fromItems;

  const hasFlatEntryFields =
    resolveEntryPrompt(llmModel) ||
    llmModel.prompt_outputs ||
    llmModel.model_metadata?.prompt_outputs ||
    (Array.isArray(llmModel.model_metadata?.examples) && llmModel.model_metadata.examples.length > 0) ||
    llmModel.file_url ||
    llmModel.file_name;

  return hasFlatEntryFields ? [llmModel] : [];
};

const isPromptOnlyEntry = (entry: any): boolean => {
  const prompt = resolveEntryPrompt(entry);
  if (!prompt) return false;
  const outputs = normalizePromptOutputs(
    entry?.prompt_outputs ?? entry?.model_metadata?.prompt_outputs ?? entry?.model_metadata?.examples
  );
  return outputs.length === 0;
};

const flattenLlmUploadEntries = (llmModels: any[]): Array<{ llmModel: any; entries: any[] }> =>
  llmModels
    .map(llmModel => ({
      llmModel,
      entries: collectLlmModelEntries(llmModel)
    }))
    .filter(group => group.entries.length > 0);

/**
 * Ensure explainability-llm receives grouped models with a `data` array when uploads are flat.
 * Coalesce many prompt-only rows (LS.Prompt(80).json style) into one model with `data: [...]`.
 */
const buildLlmModelsForExplainability = (llmModels: any[]): any[] => {
  const grouped = llmModels.map(llmModel => {
    const entries = collectLlmModelEntries(llmModel);
    if (Array.isArray(llmModel?.data) && llmModel.data.length > 0) return llmModel;
    if (entries.length === 0) return llmModel;
    return { ...llmModel, data: entries };
  });

  const shouldCoalescePromptOnlyBatch =
    grouped.length > 1 &&
    grouped.every(model => {
      const entries = collectLlmModelEntries(model);
      return entries.length === 1 && isPromptOnlyEntry(entries[0]);
    });

  if (shouldCoalescePromptOnlyBatch) {
    const first = grouped[0];
    return [
      {
        ...first,
        title: first?.title || first?.tag || first?.file_name || 'LLM Model',
        data: grouped.flatMap(model => collectLlmModelEntries(model))
      }
    ];
  }

  return grouped;
};

/**
 * Build LLM analysis rows for bias and prompt-injection-llm-rest.
 * Uses the same entry resolution as bias-detection-llm-direct so prompt-only `data: [{ prompt }]` is included.
 */
const buildLlmAnalysisEntries = (
  llmModels: any[],
  resolvedProjectId: string,
  resolvedUserId: string,
  resolvedDomain: string
): any[] =>
  flattenLlmUploadEntries(llmModels).flatMap(({ llmModel, entries }) => {
    const llmDomain = llmModel?.domain || entries[0]?.domain || resolvedDomain;
    const llmTitle = llmModel?.title || llmModel?.tag || entries[0]?.title || 'LLM Model';

    return entries
      .map((item: any) => {
        const normalizedItem = item || {};
        const row = normalizedItem as Record<string, unknown>;
        const prompt = resolveEntryPrompt(normalizedItem);
        const innerMeta = unwrapLlmModelMetadata(row);
        const outerMeta = row.model_metadata as Record<string, unknown> | undefined;
        const model_metadata = innerMeta ?? outerMeta;
        const file_url = resolveEntryFileUrl(row);
        const prompt_outputs = normalizePromptOutputs(
          normalizedItem?.prompt_outputs ??
            outerMeta?.prompt_outputs ??
            innerMeta?.prompt_outputs ??
            innerMeta?.examples ??
            outerMeta?.examples
        );

        // Prompt-injection-llm-rest requires prompt and/or prompt_outputs in each row
        if (!prompt && prompt_outputs.length === 0 && !file_url) {
          return null;
        }

        return {
          ...normalizedItem,
          projectId:
            normalizedItem.projectId && normalizedItem.projectId !== '' ? normalizedItem.projectId : resolvedProjectId,
          userId: normalizedItem.userId && normalizedItem.userId !== '' ? normalizedItem.userId : resolvedUserId,
          domain: normalizedItem.domain && normalizedItem.domain !== '' ? normalizedItem.domain : llmDomain,
          title: normalizedItem.title && normalizedItem.title !== '' ? normalizedItem.title : llmTitle,
          prompt: prompt ?? '',
          file_url: file_url ?? normalizedItem.file_url,
          model_metadata: model_metadata ?? undefined,
          prompt_outputs
        };
      })
      .filter(Boolean);
  });

/**
 * Never fall back to the project's title here — the version history must show the uploaded LLM's
 * own title (file name / tag / entry title), not the project name. Use a per-upload index label
 * instead when the upload itself carries no identifiable title, mirroring `ML Model ${idx + 1}`.
 */
const resolveLlmGroupTitle = (llmModel: any, entries: any[], idx: number): string =>
  llmModel?.title || llmModel?.tag || llmModel?.file_name || entries[0]?.title || `LLM Model ${idx + 1}`;

type LlmUploadGroup = {
  llmModel: any;
  title: string;
  entries: any[];
};

/** One group per LLM upload so batch analysis uses each upload's title (not the first copilot upload). */
const buildLlmUploadGroups = (
  llmModels: any[],
  resolvedProjectId: string,
  resolvedUserId: string,
  resolvedDomain: string
): LlmUploadGroup[] =>
  flattenLlmUploadEntries(llmModels)
    .map(({ llmModel }, idx) => {
      const entries = buildLlmAnalysisEntries([llmModel], resolvedProjectId, resolvedUserId, resolvedDomain);
      return {
        llmModel,
        title: resolveLlmGroupTitle(llmModel, entries, idx),
        entries
      };
    })
    .filter(group => group.entries.length > 0);

const postPDCAssessment = async (options: {
  resolvedProjectId: string;
  resolvedUserId: string;
  resolvedDomain: string;
  resolvedMeaid: string;
  is_rerun?: boolean;
  title: string;
  data?: unknown[];
  onStatusUpdate?: (status: string) => void;
}): Promise<void> => {
  const { resolvedProjectId, resolvedUserId, resolvedDomain, resolvedMeaid, is_rerun, title, data, onStatusUpdate } =
    options;

  onStatusUpdate?.('Executing Prompt Defense Compliance (PDC+)');
  await api.post(
    '/project/features/prompt-injection',
    {
      projectId: resolvedProjectId,
      userId: resolvedUserId,
      domain: resolvedDomain,
      title,
      meaid: resolvedMeaid,
      is_rerun,
      ...(Array.isArray(data) && data.length > 0 ? { data } : {})
    },
    PROJECT_ANALYSIS_REQUEST_CONFIG
  );
};

const buildExplainabilityPayload = (
  mlModels: any[],
  projectId: string,
  userId: string,
  domain: string,
  fallbackTitle?: string | null
) => {
  const normalizedModels = mlModels.map((model: any, idx: number) => {
    const title = model?.title || model?.tag || model?.file_name || `ML Model ${idx + 1}`;

    return {
      ...model,
      projectId: model?.projectId && model.projectId !== '' ? model.projectId : projectId,
      userId: model?.userId && model.userId !== '' ? model.userId : userId,
      domain: model?.domain && model.domain !== '' ? model.domain : domain,
      title,
      upload_date: normalizeTimestamp(model?.upload_date),
      update_date: normalizeTimestamp(model?.update_date)
    };
  });

  const primaryTitle = normalizedModels[0]?.title || fallbackTitle || 'Explainability';

  return {
    title: `Explainability – ${primaryTitle}`,
    data: normalizedModels
  };
};

interface RunProjectAnalysisOptions {
  projectId: string;
  userId: string;
  meaid?: string;
  is_rerun?: boolean;
  projectData?: ProjectData;
  activeProject?: { domain?: string; title?: string };
  selectedProject?: { domain?: string; title?: string };
  onStatusUpdate?: (status: string) => void;
  fetchProjectData?: (projectId: string) => Promise<ProjectData>;
}

export interface ProjectAnalysisResult {
  failedSteps: string[];
}

export const getAnalysisCompletionToast = (failedSteps: string[]) => ({
  title: failedSteps.length === 0 ? 'Process Completed' : 'Process Completed with Errors',
  message:
    failedSteps.length === 0
      ? 'All analysis executed successfully.'
      : `Some steps failed (${failedSteps.join(', ')}). Remaining analyses were still executed.`
});

/** Post-run polls so late Mongo writes (e.g. long Policy Violation) still reach the UI. */
export const PROJECT_RESULTS_POLL_DELAYS_MS = [3_000, 15_000, 45_000, 120_000] as const;

export const scheduleProjectResultsRefresh = (
  refresh: () => void | Promise<void>,
  delaysMs: readonly number[] = PROJECT_RESULTS_POLL_DELAYS_MS
): void => {
  delaysMs.forEach(delayMs => {
    setTimeout(() => {
      void Promise.resolve(refresh()).catch(error => {
        console.error('Delayed project results refresh failed:', error);
      });
    }, delayMs);
  });
};

export const runProjectAnalysis = async (options: RunProjectAnalysisOptions): Promise<ProjectAnalysisResult> => {
  const {
    projectId,
    userId,
    projectData: providedProjectData,
    activeProject,
    selectedProject,
    onStatusUpdate,
    fetchProjectData
  } = options;

  if (!projectId || !userId) {
    throw new Error('Project or user information is missing.');
  }

  // Fetch project data if not provided
  let projectData: ProjectData;
  if (providedProjectData) {
    projectData = providedProjectData;
  } else if (fetchProjectData) {
    onStatusUpdate?.('Fetching Project Data...');
    projectData = await fetchProjectData(projectId);
  } else {
    throw new Error('Project data or fetchProjectData function is required.');
  }

  const resolvedProjectId = projectData?.projectId || projectId;
  if (!options.is_rerun && resolvedProjectId) {
    onStatusUpdate?.('Loading existing analysis results...');
    projectData = (await mergeProjectResultsIntoProjectData(
      resolvedProjectId,
      projectData as Record<string, unknown>
    )) as ProjectData;
  }

  const uploads = projectData?.uploads || {};
  const allDataset = asUploadArray((uploads as any).dataset);
  const allMlModel = asUploadArray((uploads as any).ml_model);
  const normalizedLlmModels = dedupeLlmUploadRecords(
    expandLlmUploadRecords([
      ...asUploadArray((uploads as any).llm_models),
      ...asUploadArray((uploads as any).llm_model),
      ...asUploadArray((uploads as any).LLM_MODEL)
    ])
  );

  const analyzedUploadState = options.is_rerun
    ? { uploadIds: new Set<string>(), baseTitles: new Set<string>(), fileKeys: new Set<string>() }
    : collectAnalyzedUploadState(projectData as any);
  const dataset = options.is_rerun ? allDataset : filterPendingUploads(allDataset, analyzedUploadState, item => item);
  const ml_model = options.is_rerun ? allMlModel : filterPendingUploads(allMlModel, analyzedUploadState, item => item);

  const resolvedUserId = projectData?.userId || userId;
  const resolvedDomain =
    activeProject?.domain || selectedProject?.domain || projectData?.domain || normalizedLlmModels[0]?.domain;

  if (!resolvedProjectId || !resolvedUserId) {
    throw new Error('Missing project or user identifiers.');
  }

  const resolvedMeaid = options.meaid || uuidv4();

  // Resolve title from various sources
  const resolvedTitle =
    projectData?.title ||
    activeProject?.title ||
    selectedProject?.title ||
    ml_model[0]?.title ||
    ml_model[0]?.tag ||
    normalizedLlmModels[0]?.title ||
    normalizedLlmModels[0]?.tag ||
    'Project';

  const basePayload = {
    projectId: resolvedProjectId,
    userId: resolvedUserId,
    domain: resolvedDomain,
    title: resolvedTitle,
    meaid: resolvedMeaid
  };

  let llmUploadGroups = buildLlmUploadGroups(normalizedLlmModels, resolvedProjectId, resolvedUserId, resolvedDomain);

  if (!options.is_rerun) {
    llmUploadGroups = llmUploadGroups.filter(group => !isLlmUploadGroupAlreadyAnalyzed(group, analyzedUploadState));
  }

  const isLlmOnlyUpload = dataset.length === 0 && ml_model.length === 0 && llmUploadGroups.length > 0;
  let pdcLlmDone = false;
  const failedSteps: string[] = [];

  const runStep = async (stepName: string, action: () => Promise<void>) => {
    try {
      await action();
    } catch (error) {
      console.error(`${stepName} failed:`, error);
      failedSteps.push(stepName);
    }
  };

  // LLM-only: PDC+ assessment first — one run per upload (keeps each Copilot / email title)
  if (isLlmOnlyUpload && llmUploadGroups.length > 0) {
    for (const group of llmUploadGroups) {
      const stepName = `Prompt Injection PDC (${group.title})`;
      await runStep(stepName, () =>
        postPDCAssessment({
          resolvedProjectId,
          resolvedUserId,
          resolvedDomain,
          resolvedMeaid,
          is_rerun: options.is_rerun,
          title: group.title,
          data: group.entries,
          onStatusUpdate
        })
      );
    }
    pdcLlmDone = true;
  }

  // Bias Detection - ML
  if (dataset.length > 0 || ml_model.length > 0) {
    const { title: mlPayloadTitle, data: mlDataEntries } = buildMlBiasPayload(
      ml_model,
      dataset,
      resolvedProjectId,
      resolvedUserId,
      resolvedDomain,
      projectData?.title || activeProject?.title || selectedProject?.title
    );

    if (mlDataEntries.length > 0) {
      await runStep('Bias Detection - ML', async () => {
        onStatusUpdate?.('Executing Bias Detection - ML');
        await api.post(
          '/project/features/bias-detection-ml-rest',
          {
            projectId: resolvedProjectId,
            userId: resolvedUserId,
            domain: resolvedDomain,
            title: mlPayloadTitle,
            meaid: resolvedMeaid,
            is_rerun: options.is_rerun,
            data: mlDataEntries
          },
          PROJECT_ANALYSIS_REQUEST_CONFIG
        );
      });
    }
  }

  // Bias Detection - LLM (one run per upload so version history keeps each copilot title)
  if (llmUploadGroups.length > 0) {
    onStatusUpdate?.('Executing Bias Detection - LLM');
    for (const group of llmUploadGroups) {
      const stepName = `Bias Detection - LLM (${group.title})`;
      await runStep(stepName, () =>
        api.post(
          '/project/features/bias-detection-llm-direct',
          {
            projectId: resolvedProjectId,
            userId: resolvedUserId,
            domain: group.entries[0]?.domain || resolvedDomain,
            title: group.title,
            meaid: resolvedMeaid,
            is_rerun: options.is_rerun,
            data: group.entries
          },
          PROJECT_ANALYSIS_REQUEST_CONFIG
        )
      );
    }
  }

  // Fairness Matrix — handles ML + LLM lanes based on available uploads (no separate fairness-llm-rest)
  if (dataset.length > 0 || ml_model.length > 0 || llmUploadGroups.length > 0) {
    await runStep('Fairness Matrix', async () => {
      onStatusUpdate?.('Executing Fairness Matrix');
      await api.post(
        '/project/features/fairness-matrix',
        {
          ...basePayload,
          is_rerun: options.is_rerun,
          dataset: dataset || [],
          ml_model: ml_model || [],
          llm_models:
            llmUploadGroups.length > 0
              ? buildLlmModelsForExplainability(llmUploadGroups.map(group => group.llmModel))
              : []
        },
        PROJECT_ANALYSIS_REQUEST_CONFIG
      );
    });
  }

  // Explainability - ML
  if (ml_model.length > 0) {
    const { title: explainabilityTitle, data: explainabilityData } = buildExplainabilityPayload(
      ml_model,
      resolvedProjectId,
      resolvedUserId,
      resolvedDomain,
      projectData?.title || activeProject?.title || selectedProject?.title
    );

    if (explainabilityData.length > 0) {
      await runStep('Explainability - ML', async () => {
        onStatusUpdate?.('Executing Explainability');
        await api.post(
          '/project/features/explainability-ml-rest',
          {
            projectId: resolvedProjectId,
            userId: resolvedUserId,
            title: explainabilityTitle,
            meaid: resolvedMeaid,
            is_rerun: options.is_rerun,
            data: explainabilityData
          },
          PROJECT_ANALYSIS_REQUEST_CONFIG
        );
      });
    }
  }

  // Explainability - LLM (one run per upload so version history keeps each copilot title)
  if (llmUploadGroups.length > 0) {
    onStatusUpdate?.('Executing Explainability - LLM');
    for (const group of llmUploadGroups) {
      const stepName = `Explainability - LLM (${group.title})`;
      await runStep(stepName, () =>
        api.post(
          '/project/features/explainability-llm',
          {
            projectId: resolvedProjectId,
            userId: resolvedUserId,
            domain: group.entries[0]?.domain || resolvedDomain,
            title: group.title,
            meaid: resolvedMeaid,
            is_rerun: options.is_rerun,
            llm_models: buildLlmModelsForExplainability([group.llmModel])
          },
          PROJECT_ANALYSIS_REQUEST_CONFIG
        )
      );
    }
  }

  // AI Safety — single call; backend runs ML + LLM lanes from available uploads (like fairness-matrix)
  if (dataset.length > 0 || ml_model.length > 0 || llmUploadGroups.length > 0) {
    await runStep('AI Safety Detection', async () => {
      onStatusUpdate?.('Executing AI Safety Detection');
      await api.post(
        '/project/features/ai-safety',
        {
          ...basePayload,
          is_rerun: options.is_rerun,
          dataset: dataset || [],
          ml_model: ml_model || [],
          llm_models:
            llmUploadGroups.length > 0
              ? buildLlmModelsForExplainability(llmUploadGroups.map(group => group.llmModel))
              : []
        },
        PROJECT_ANALYSIS_REQUEST_CONFIG
      );
    });
  }

  // Prompt Defense Compliance (PDC+) — LLM-only, one run per upload (Bias / Copilot pattern)
  if (!pdcLlmDone && llmUploadGroups.length > 0) {
    for (const group of llmUploadGroups) {
      const stepName = `Prompt Injection PDC (${group.title})`;
      await runStep(stepName, () =>
        postPDCAssessment({
          resolvedProjectId,
          resolvedUserId,
          resolvedDomain,
          resolvedMeaid,
          is_rerun: options.is_rerun,
          title: group.title,
          data: group.entries,
          onStatusUpdate
        })
      );
    }
  }

  // Policy Extractor - REMOVED: No longer called on run/complete button click

  // Ontology Generator (after all analysis APIs complete)
  await runStep('Ontology', async () => {
    onStatusUpdate?.('Generating Ontology');
    await api.post(
      '/ontology/project/features/ontology',
      {
        projectId: resolvedProjectId,
        userId: resolvedUserId,
        meaid: resolvedMeaid,
        is_rerun: options.is_rerun
      },
      PROJECT_ANALYSIS_REQUEST_CONFIG
    );
  });

  // OWASP Controls Generator (after all analysis APIs complete)
  await runStep('OWASP Controls', async () => {
    onStatusUpdate?.('Generating OWASP Controls');
    await api.post(
      '/project/features/owasp',
      {
        projectId: resolvedProjectId,
        userId: resolvedUserId,
        meaid: resolvedMeaid,
        is_rerun: options.is_rerun
      },
      PROJECT_ANALYSIS_REQUEST_CONFIG
    );
  });

  // Internal Compliance Generator (after OWASP and Ontology APIs complete)
  await runStep('Internal Compliance', async () => {
    onStatusUpdate?.('Generating Internal Compliance');
    await api.post(
      '/project/features/internal-compliance',
      {
        projectId: resolvedProjectId,
        userId: resolvedUserId,
        title: resolvedTitle,
        meaid: resolvedMeaid,
        is_rerun: options.is_rerun
      },
      PROJECT_ANALYSIS_REQUEST_CONFIG
    );
  });

  // Policy Violation Generator (evaluates prompt/output pairs against canonical policies)
  await runStep('Policy Violation', async () => {
    onStatusUpdate?.('Evaluating Policy Violations');
    await api.post(
      '/project/features/internal-policy-compliance',
      {
        projectId: resolvedProjectId,
        userId: resolvedUserId,
        title: resolvedTitle,
        meaid: resolvedMeaid,
        is_rerun: options.is_rerun
      },
      POLICY_VIOLATION_REQUEST_CONFIG
    );
  });

  // External Compliance Generator (after Internal Compliance API complete)
  await runStep('External Compliance', async () => {
    onStatusUpdate?.('Generating External Compliance');
    await api.post(
      '/project/features/external-compliance',
      {
        projectId: resolvedProjectId,
        userId: resolvedUserId,
        title: resolvedTitle,
        meaid: resolvedMeaid,
        is_rerun: options.is_rerun
      },
      PROJECT_ANALYSIS_REQUEST_CONFIG
    );
  });

  return { failedSteps };
};
